// eslint-disable-next-line
import React, { ReactNode, useState } from 'react';
import {
    Button,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grow,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from '@material-ui/core';
import { User } from '@nal/models/User.model';
import { State, History } from 'history';
import _ from 'lodash';
import { connect, useDispatch } from 'react-redux';
import styles from './CurrentUser.module.scss';
import DownIcon from '@material-ui/icons/ExpandMore';
import actions from '../Auth/store/actions';
import avatar from './default.jpg';
import { Dispatch } from 'redux';
import { Formik, FormikHelpers, Form, Field, FormikState } from 'formik';

import * as Yup from 'yup';
import { TextField } from 'formik-material-ui';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { UserService } from '@nal/services/User.service';
import NalAlert, { AlertControlType } from '../NalAlert/NalAlert';
import { Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { useHistory } from 'react-router-dom';

const alertControl$: Subject<AlertControlType> = new Subject();

const userService = new UserService();

const logout = (dispatch: Dispatch<any>, history: History) => {
    dispatch({
        type: actions.LOGOUT,
        history,
    });
};

const changePassword = (
    values: passwordFormValues,
    setSubmitting: (isSubmitting: boolean) => void,
    resetForm: (nextState?: Partial<FormikState<passwordFormValues>>) => void
) => {
    setSubmitting(true);

    userService
        .changePassword(values)
        .pipe(finalize(() => setSubmitting(false)))
        .subscribe({
            next: (res) => {
                resetForm();

                alertControl$.next({
                    open: true,
                    data: {
                        message: 'パスワードが変更されました。',
                        type: 'success',
                        duration: 3000,
                    },
                });
            },
            error: () => {
                alertControl$.next({
                    open: true,
                    data: {
                        message: 'エラーが発生しました。',
                        type: 'error',
                        duration: 3000,
                    },
                });
            },
        });
};

const changePasswordFormValidator = Yup.object({
    new_password: Yup.string().required('新しいパスワードは、必ず指定してください。'),
    new_password_confirmation: Yup.string().oneOf(
        [Yup.ref('new_password'), undefined],
        'パスワードがパスワード（確認）と一致していません。'
    ),
    old_password: Yup.string().required('現在パスワードは、必ず指定してください。'),
});

type passwordFormValues = {
    new_password: string;
    new_password_confirmation: string;
    old_password: string;
};

type CurrentUserPropType = {
    children?: ReactNode;
    me?: User;
};

const CurrentUser: React.FC<CurrentUserPropType> = ({ me }) => {
    const [open, setOpen] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    const [showPasswordFormOpen, setShowPasswordFormOpen] = useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const dispatch = useDispatch();
    const history = useHistory();

    return (
        <div className={styles.CurrentUser}>
            {me && (
                <Button
                    ref={anchorRef}
                    aria-controls="menu-list-grow"
                    aria-haspopup="true"
                    onClick={() => setOpen(!open)}
                    className={styles.InfoButton}
                >
                    <img src={me?.avatar_url || avatar} alt={me?.name || ' '}></img>
                    <span>{me?.name || ''}</span>
                    <DownIcon />
                </Button>
            )}
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: 'left bottom',
                            width: anchorRef.current?.clientWidth + 'px',
                            minWidth: '150px',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList autoFocusItem={open} id="menu-list-grow">
                                    <MenuItem onClick={() => setShowPasswordFormOpen(true)}>パスワード変更</MenuItem>
                                    <MenuItem onClick={() => logout(dispatch, history)}>ログアウト</MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <Dialog
                className={styles.ChangePasswordDialog}
                onClose={(e, r) => setShowPasswordFormOpen(false)}
                open={showPasswordFormOpen}
            >
                <DialogTitle>パスワード変更</DialogTitle>
                <DialogContent className={styles.ChangePasswordDialogContent}>
                    <Formik
                        className={styles.ChangePasswordForm}
                        initialValues={{
                            new_password: '',
                            new_password_confirmation: '',
                            old_password: '',
                        }}
                        validationSchema={changePasswordFormValidator}
                        enableReinitialize
                        onSubmit={(
                            values: passwordFormValues,
                            { setSubmitting, resetForm }: FormikHelpers<passwordFormValues>
                        ) => {
                            changePassword(values, setSubmitting, resetForm);
                        }}
                    >
                        {({ values, setFieldValue, submitForm, errors, touched }) => {
                            return (
                                <Form className={styles.PasswordForm}>
                                    <Field
                                        component={TextField}
                                        fullWidth={true}
                                        name="old_password"
                                        label="現在パスワード"
                                        type={showOldPassword ? 'text' : 'password'}
                                        autoComplete="off"
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: showOldPassword ? (
                                                <IconButton onClick={() => setShowOldPassword(false)}>
                                                    <VisibilityOff />
                                                </IconButton>
                                            ) : (
                                                <IconButton onClick={() => setShowOldPassword(true)}>
                                                    <Visibility />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                    <Field
                                        component={TextField}
                                        fullWidth={true}
                                        name="new_password"
                                        label="新しいパスワード"
                                        type={showNewPassword ? 'text' : 'password'}
                                        autoComplete="off"
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: showNewPassword ? (
                                                <IconButton onClick={() => setShowNewPassword(false)}>
                                                    <VisibilityOff />
                                                </IconButton>
                                            ) : (
                                                <IconButton onClick={() => setShowNewPassword(true)}>
                                                    <Visibility />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                    <Field
                                        component={TextField}
                                        fullWidth={true}
                                        name="new_password_confirmation"
                                        label="新しいパスワード（確認）"
                                        type={showNewPasswordConfirmation ? 'text' : 'password'}
                                        autoComplete="off"
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: showNewPasswordConfirmation ? (
                                                <IconButton onClick={() => setShowNewPasswordConfirmation(false)}>
                                                    <VisibilityOff />
                                                </IconButton>
                                            ) : (
                                                <IconButton onClick={() => setShowNewPasswordConfirmation(true)}>
                                                    <Visibility />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                    <div className={styles.Action}>
                                        <Button variant="outlined" onClick={() => setShowPasswordFormOpen(false)}>
                                            キャンセル
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            type="submit"
                                            onClick={() => submitForm()}
                                        >
                                            更新
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
            <NalAlert control={alertControl$}></NalAlert>
        </div>
    );
};

function mapStateToProps(state: State) {
    return {
        me: _.get(state, 'Me'),
    };
}

export default connect(mapStateToProps)(CurrentUser);
